import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Upload, X, CheckCircle2, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface FileUploadStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

interface BulkFileUploadProps {
  locationId: string;
  bucket: 'location-businesscases' | 'location-documents';
  tableName: 'location_businesscase_files' | 'location_documents';
  documentType?: string;
  onUploadComplete?: () => void;
}

export function BulkFileUpload({
  locationId,
  bucket,
  tableName,
  documentType,
  onUploadComplete,
}: BulkFileUploadProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<FileUploadStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    const validFiles = selectedFiles.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: `${file.name} is te groot`,
          description: 'Maximale bestandsgrootte is 10MB',
          variant: 'destructive',
        });
        return false;
      }
      return true;
    });

    setFiles(validFiles.map(file => ({
      file,
      status: 'pending',
      progress: 0,
    })));
  };

  const uploadSingleFile = async (fileStatus: FileUploadStatus, index: number) => {
    const { file } = fileStatus;
    
    try {
      setFiles(prev => prev.map((f, i) => 
        i === index ? { ...f, status: 'uploading', progress: 10 } : f
      ));

      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}/${locationId}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      setFiles(prev => prev.map((f, i) => 
        i === index ? { ...f, progress: 50 } : f
      ));

      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from(bucket)
        .createSignedUrl(fileName, 60 * 60 * 24 * 7);

      if (signedUrlError) {
        await supabase.storage.from(bucket).remove([fileName]);
        throw signedUrlError;
      }

      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

      setFiles(prev => prev.map((f, i) => 
        i === index ? { ...f, progress: 75 } : f
      ));

      if (tableName === 'location_businesscase_files') {
        const { error: dbError } = await supabase
          .from('location_businesscase_files')
          .insert({
            location_id: locationId,
            file_name: file.name,
            file_url: signedUrlData.signedUrl,
            storage_path: fileName,
            url_expires_at: expiresAt,
            file_size: file.size,
            uploaded_by: user?.id,
          });

        if (dbError) {
          await supabase.storage.from(bucket).remove([fileName]);
          throw dbError;
        }
      } else {
        const { error: dbError } = await supabase
          .from('location_documents')
          .insert({
            location_id: locationId,
            document_name: file.name,
            document_url: signedUrlData.signedUrl,
            storage_path: fileName,
            url_expires_at: expiresAt,
            document_type: documentType || 'other',
            file_size: file.size,
            uploaded_by: user?.id,
          });

        if (dbError) {
          await supabase.storage.from(bucket).remove([fileName]);
          throw dbError;
        }
      }

      setFiles(prev => prev.map((f, i) => 
        i === index ? { ...f, status: 'success', progress: 100 } : f
      ));
    } catch (error: any) {
      setFiles(prev => prev.map((f, i) => 
        i === index ? { ...f, status: 'error', progress: 0, error: error.message } : f
      ));
    }
  };

  const handleBulkUpload = async () => {
    setIsUploading(true);

    const batchSize = 3;
    for (let i = 0; i < files.length; i += batchSize) {
      const batch = files.slice(i, i + batchSize);
      await Promise.all(
        batch.map((fileStatus, batchIndex) => 
          uploadSingleFile(fileStatus, i + batchIndex)
        )
      );
    }

    setIsUploading(false);
    
    queryClient.invalidateQueries({ 
      queryKey: tableName === 'location_businesscase_files' 
        ? ['location-businesscase-files'] 
        : ['location-documents'] 
    });

    const successCount = files.filter(f => f.status === 'success').length;
    const errorCount = files.filter(f => f.status === 'error').length;

    toast({
      title: 'Upload voltooid',
      description: `${successCount} bestanden geüpload${errorCount > 0 ? `, ${errorCount} gefaald` : ''}`,
    });

    if (onUploadComplete) onUploadComplete();
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id={`bulk-upload-${bucket}`}
          accept=".pdf,.xlsx,.xls,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp"
        />
        <label htmlFor={`bulk-upload-${bucket}`}>
          <Button variant="outline" className="cursor-pointer" asChild>
            <span>
              <Upload className="w-4 h-4 mr-2" />
              Selecteer meerdere bestanden
            </span>
          </Button>
        </label>
        {files.length > 0 && (
          <Button 
            onClick={handleBulkUpload} 
            disabled={isUploading}
          >
            Upload {files.length} bestand{files.length > 1 ? 'en' : ''}
          </Button>
        )}
      </div>

      {files.length > 0 && (
        <div className="space-y-2 border rounded-lg p-4">
          <h4 className="font-medium text-sm">Upload Wachtrij</h4>
          {files.map((fileStatus, index) => (
            <div 
              key={index} 
              className="flex items-center gap-3 p-2 bg-muted rounded"
            >
              <div className="flex-shrink-0">
                {fileStatus.status === 'pending' && (
                  <div className="w-5 h-5 rounded-full border-2 border-muted-foreground" />
                )}
                {fileStatus.status === 'uploading' && (
                  <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                )}
                {fileStatus.status === 'success' && (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                )}
                {fileStatus.status === 'error' && (
                  <AlertCircle className="w-5 h-5 text-destructive" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {fileStatus.file.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(fileStatus.file.size)}
                  {fileStatus.error && ` • ${fileStatus.error}`}
                </p>
                {fileStatus.status === 'uploading' && (
                  <Progress value={fileStatus.progress} className="h-1 mt-1" />
                )}
              </div>

              {fileStatus.status === 'pending' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeFile(index)}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
